import React, { useState, useEffect } from 'react';
import { useMargins } from './MarginContext';
import './MarginControls.css';

const MarginControls: React.FC = () => {
  const { margins, setMargins, resetToDefaults } = useMargins();
  
  // Local state for input values to allow free typing
  const [verticalInput, setVerticalInput] = useState(margins.top.toString());
  const [horizontalInput, setHorizontalInput] = useState(margins.left.toString());

  // Sync local state when margins change externally (e.g., reset)
  useEffect(() => {
    setVerticalInput(margins.top.toString());
  }, [margins.top]);

  useEffect(() => {
    setHorizontalInput(margins.left.toString());
  }, [margins.left]);

  const handleVerticalChange = (value: string) => {
    setVerticalInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0.1 && numValue <= 1.5) {
      setMargins({
        ...margins,
        top: numValue,
        bottom: numValue,
      });
    }
  };

  const handleHorizontalChange = (value: string) => {
    setHorizontalInput(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0.1 && numValue <= 1.5) {
      setMargins({
        ...margins,
        left: numValue,
        right: numValue,
      });
    }
  };

  const handleVerticalBlur = () => {
    const numValue = parseFloat(verticalInput);
    if (isNaN(numValue) || numValue < 0.1) {
      setVerticalInput('0.1');
      setMargins({ ...margins, top: 0.1, bottom: 0.1 });
    } else if (numValue > 1.5) {
      setVerticalInput('1.5');
      setMargins({ ...margins, top: 1.5, bottom: 1.5 });
    }
  };

  const handleHorizontalBlur = () => {
    const numValue = parseFloat(horizontalInput);
    if (isNaN(numValue) || numValue < 0.1) {
      setHorizontalInput('0.1');
      setMargins({ ...margins, left: 0.1, right: 0.1 });
    } else if (numValue > 1.5) {
      setHorizontalInput('1.5');
      setMargins({ ...margins, left: 1.5, right: 1.5 });
    }
  };

  return (
    <div className="margin-controls">
      <div className="margin-controls__group">
        <div className="margin-controls__item">
          <div className="margin-controls__icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 3v18M5 6l7-3 7 3M5 18l7 3 7-3" />
            </svg>
          </div>
          <span className="margin-controls__label">Top/Bottom</span>
          <div className="margin-controls__input-wrapper">
            <input
              type="number"
              min="0.1"
              max="1.5"
              step="0.1"
              value={verticalInput}
              onChange={(e) => handleVerticalChange(e.target.value)}
              onBlur={handleVerticalBlur}
              className="margin-controls__input"
            />
            <span className="margin-controls__unit">in</span>
          </div>
        </div>

        <div className="margin-controls__divider" />

        <div className="margin-controls__item">
          <div className="margin-controls__icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12h18M6 5l-3 7 3 7M18 5l3 7-3 7" />
            </svg>
          </div>
          <span className="margin-controls__label">Left/Right</span>
          <div className="margin-controls__input-wrapper">
            <input
              type="number"
              min="0.1"
              max="1.5"
              step="0.1"
              value={horizontalInput}
              onChange={(e) => handleHorizontalChange(e.target.value)}
              onBlur={handleHorizontalBlur}
              className="margin-controls__input"
            />
            <span className="margin-controls__unit">in</span>
          </div>
        </div>

        <button
          type="button"
          className="margin-controls__reset"
          onClick={resetToDefaults}
          title="Reset to defaults"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MarginControls;
